const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;

const MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  zip: "application/zip",
  csv: "text/csv;charset=utf-8;",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  txt: "text/plain",
  xer: "application/octet-stream",
};

const sanitizeBaseName = (filename) =>
  String(filename ?? "")
    .replace(INVALID_FILENAME_CHARS, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.+$/g, "") || "download";

export const withFileExtension = (filename, extension) => {
  const ext = String(extension || "").replace(/^\./, "").trim();
  const sanitized = sanitizeBaseName(filename);
  if (!ext) {
    return sanitized;
  }
  if (sanitized.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
    return sanitized;
  }
  return `${sanitized}.${ext}`;
};

const getExtension = (filename) => {
  const sanitized = sanitizeBaseName(filename);
  const lastDot = sanitized.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === sanitized.length - 1) {
    return "";
  }
  return sanitized.slice(lastDot + 1).toLowerCase();
};

const blobForDownload = (blobData, filename) => {
  const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
  if (blob.type) {
    return blob;
  }
  const mime = MIME_BY_EXTENSION[getExtension(filename)];
  return mime ? new Blob([blob], { type: mime }) : blob;
};

/**
 * Trigger a same-origin blob download. Safari ignores `download` on detached
 * anchors and on cross-origin URLs, then names the file after the site host
 * (e.g. "submittallink.com") instead of the intended .pdf/.zip name.
 */
export const downloadBlob = (blobData, filename = "download") => {
  const safeName = sanitizeBaseName(filename);
  const blob = blobForDownload(blobData, safeName);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeName;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  // Safari may still be reading the blob if the object URL is revoked immediately.
  window.setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(url);
  }, 2000);
};

export const filenameFromUrl = (fileUrl, fallback = "download") => {
  try {
    const path = new URL(fileUrl, window.location.origin).pathname;
    const lastSegment = decodeURIComponent(path.split("/").filter(Boolean).pop() || "");
    return sanitizeBaseName(lastSegment) || fallback;
  } catch {
    return fallback;
  }
};

/**
 * Direct navigation used when fetch() cannot read the file (missing CORS on
 * S3/object storage). Cross-origin `download` is ignored by the browser, so
 * the file still opens; Safari may then name it after the host.
 */
const navigateToUrl = (fileUrl, filename) => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  if (filename) {
    link.download = sanitizeBaseName(filename);
  }
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadFromUrl = async (fileUrl, filename) => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error("Failed to download file");
    }
    const blob = await response.blob();
    downloadBlob(blob, filename || filenameFromUrl(fileUrl));
  } catch {
    navigateToUrl(fileUrl, filename);
  }
};

export const downloadCSV = (headers, rows, filename = "export.csv") => {
  const formatCSVValue = (value) => {
    const stringValue =
      value === null || value === undefined ? "" : value.toString();
    const escapedValue = stringValue.replace(/"/g, '""');
    return /[",\n]/.test(stringValue) ? `"${escapedValue}"` : escapedValue;
  };

  const csvContent = [
    headers.map(formatCSVValue).join(","),
    ...rows.map((row) => row.map(formatCSVValue).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  downloadBlob(blob, withFileExtension(filename, "csv"));
};
